import React, { useState, useEffect } from 'react';
import { Modal } from 'reactstrap';
import Swal from "sweetalert2/dist/sweetalert2.js";

// ModalTaimesAgentMatrix — AI config for the matrix evidence column.
//
// Prompt POR FILA: each matrix row (Frenos, Luces, Llantas…) has its own prompt
// of what its photo must show. A row left blank is NOT validated (its evidence is
// accepted as-is). Agent, cancel behavior and mapeos are global to the matrix.
// On upload (row by row, direct-to-S3), the agent evaluates that row's photo with
// that row's prompt and either cancels its evidence (datos.coincide === false) or
// fills the row's comentario/valor from the mapped datos keys.
//
// Same visual language as the file-field modal (taf- styles). Saved via
// PATCH /formatos/taimes_agent_matrix_config/:question_matrix_id.
// Mapeo target here is a ROW field (comentario | valor), not another question.

const TARGETS = [
    { value: "comentario", label: "Comentario de la fila" },
    { value: "valor", label: "Valor / columna de la fila" }
];

const emptyConfig = () => ({
    enabled: false,
    agent_id: "",
    prompt: "",
    prompts: {},
    mapeos: [],
    validacion: { si_no_coincide: "nada", mensaje: "" }
});

const normalizeConfig = (raw) => {
    const c = raw && typeof raw === "object" ? raw : {};
    const v = c.validacion && typeof c.validacion === "object" ? c.validacion : {};
    return {
        enabled: !!c.enabled,
        agent_id: c.agent_id || "",
        prompt: c.prompt || "",
        prompts: (c.prompts && typeof c.prompts === "object") ? { ...c.prompts } : {},
        mapeos: Array.isArray(c.mapeos)
            ? c.mapeos.map(m => ({ clave: (m && m.clave) || "", target: (m && m.target) || "" }))
            : [],
        validacion: {
            si_no_coincide: v.si_no_coincide || "nada",
            mensaje: v.mensaje || ""
        }
    };
};

const ModalTaimesAgentMatrix = ({ modal, toggle, questionMatrix, onSaved }) => {
    const token = document.querySelector("[name='csrf-token']").content;
    const matrixId = questionMatrix && questionMatrix.id;

    // Filas de la matriz (Frenos, Luces, Llantas…) — cada una lleva su propio prompt.
    const rows = (questionMatrix && Array.isArray(questionMatrix.question_matrix_items))
        ? [...questionMatrix.question_matrix_items].sort((a, b) => (a.position || 0) - (b.position || 0))
        : [];

    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(emptyConfig());

    // "Probar"
    const [sampleUrl, setSampleUrl] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [datosKeys, setDatosKeys] = useState([]);
    const [testItemId, setTestItemId] = useState("");

    useEffect(() => {
        if (modal) {
            setConfig(normalizeConfig(questionMatrix ? questionMatrix.taimes_ai_config : null));
            setTestResult(null);
        }
    }, [modal, matrixId]);

    const patch = (p) => setConfig(prev => ({ ...prev, ...p }));
    const patchPrompt = (itemId, value) => setConfig(prev => ({
        ...prev, prompts: { ...prev.prompts, [String(itemId)]: value }
    }));
    const patchValidacion = (p) => setConfig(prev => ({ ...prev, validacion: { ...prev.validacion, ...p } }));
    const addMapeo = () => setConfig(prev => ({ ...prev, mapeos: [...prev.mapeos, { clave: "", target: "" }] }));
    const updateMapeo = (idx, p) => setConfig(prev => ({
        ...prev, mapeos: prev.mapeos.map((m, i) => (i === idx ? { ...m, ...p } : m))
    }));
    const removeMapeo = (idx) => setConfig(prev => ({ ...prev, mapeos: prev.mapeos.filter((_, i) => i !== idx) }));

    const runTest = () => {
        if (!config.agent_id.trim()) {
            Swal.fire({ icon: "warning", title: "Falta el agente", text: "Ingresa el id del agente antes de probar." });
            return;
        }
        if (!sampleUrl.trim()) {
            Swal.fire({ icon: "warning", title: "Falta la URL", text: "Pega la URL de un archivo de muestra." });
            return;
        }
        const testPrompt = (config.prompts[String(testItemId)] || config.prompt || "").trim();
        if (!testPrompt) {
            Swal.fire({ icon: "warning", title: "Falta el prompt", text: "Elige una fila con prompt para probar (o escríbelo primero)." });
            return;
        }
        setTesting(true);
        setTestResult(null);
        fetch(`/formatos/taimes_agent_test`, {
            method: 'POST',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
            body: JSON.stringify({ agent_id: config.agent_id.trim(), prompt: testPrompt, file_url: sampleUrl.trim() })
        })
            .then(res => res.json().then(body => ({ ok: res.ok, body })))
            .then(({ ok, body }) => {
                if (ok && body.success) {
                    const datos = body.datos && typeof body.datos === "object" ? body.datos : {};
                    setTestResult({ resumen: body.resumen || "", datos });
                    setDatosKeys(Object.keys(datos));
                } else {
                    Swal.fire({ icon: "error", title: "No se pudo probar", text: (body && body.error) || "" });
                }
            })
            .catch(() => Swal.fire({ icon: "error", title: "No se pudo probar la IA" }))
            .finally(() => setTesting(false));
    };

    const save = () => {
        if (config.enabled && !config.agent_id.trim()) {
            Swal.fire({ icon: "warning", title: "Falta el agente", text: "Ingresa el id del agente de Taimes." });
            return;
        }
        setSaving(true);
        fetch(`/formatos/taimes_agent_matrix_config/${matrixId}`, {
            method: 'PATCH',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
            body: JSON.stringify({ taimes_ai_config: config })
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    Swal.fire({ icon: "success", title: "Configuración guardada", timer: 1400, showConfirmButton: false });
                    if (onSaved) onSaved(data.taimes_ai_config);
                    if (toggle) toggle();
                } else {
                    Swal.fire({ icon: "error", title: "Error al guardar", text: data.error || "" });
                }
            })
            .catch(() => Swal.fire({ icon: "error", title: "Error al guardar" }))
            .finally(() => setSaving(false));
    };

    const datalistId = `taf-matrix-datos-keys-${matrixId}`;

    return (
        <Modal isOpen={modal} toggle={toggle} size="lg" backdrop="static"
            className="modal-dialog-centered taf-modal">
            <div className="taf-header">
                <span className="taf-header__icon"><i className="fas fa-robot"></i></span>
                <div className="taf-header__titles">
                    <h5>IA en la evidencia de la matriz</h5>
                    <div className="taf-header__sub">Aplica a la foto de cada fila</div>
                </div>
                <button type="button" className="taf-header__close" onClick={toggle}>
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <div className="taf-body">
                {/* Master switch */}
                <div className="taf-card taf-card--switch">
                    <div>
                        <div className="taf-card__title" style={{ fontWeight: 600 }}>Revisar con IA cada evidencia al subirla</div>
                        <div className="taf-card__hint">Al subir la foto de una fila, la IA la analiza y llena/valida esa fila.</div>
                    </div>
                    <div className="custom-control custom-switch taf-switch">
                        <input type="checkbox" className="custom-control-input" id={`taf-m-enabled-${matrixId}`}
                            checked={config.enabled} onChange={e => patch({ enabled: e.target.checked })} />
                        <label className="custom-control-label" htmlFor={`taf-m-enabled-${matrixId}`}>
                            {config.enabled ? "Activa" : "Inactiva"}
                        </label>
                    </div>
                </div>

                <div className={config.enabled ? "" : "taf-section-disabled"}>
                    {/* Agente */}
                    <div className="taf-card">
                        <div className="taf-field">
                            <label className="taf-label">Agente (agent_id)</label>
                            <input type="text" className="taf-input taf-input--mono" value={config.agent_id}
                                onChange={e => patch({ agent_id: e.target.value })}
                                placeholder="p. ej. a72c0293-0639-4e74-ac94-fffe302037af" />
                        </div>
                    </div>

                    {/* Prompt POR FILA */}
                    <div className="taf-card">
                        <label className="taf-label">Qué validar en cada fila</label>
                        <div className="taf-card__hint" style={{ marginBottom: 10 }}>
                            Escribe qué debe mostrar la foto de cada fila. <b>Fila en blanco = no se valida con IA</b> (su evidencia se acepta tal cual).
                        </div>
                        {rows.length === 0 && (
                            <div className="taf-empty">Esta matriz aún no tiene filas. Agrega las filas primero.</div>
                        )}
                        {rows.map(row => (
                            <div className="taf-field" key={row.id}>
                                <label className="taf-label" style={{ fontWeight: 600 }}>{row.name || `Fila ${row.position || ""}`}</label>
                                <textarea className="taf-textarea" value={config.prompts[String(row.id)] || ""}
                                    onChange={e => patchPrompt(row.id, e.target.value)}
                                    placeholder="p. ej. Debe ser una llanta y describe su estado (coincide true/false)." />
                            </div>
                        ))}
                    </div>

                    {/* Probar */}
                    <div className="taf-card taf-card--test">
                        <div className="taf-card__title">
                            <i className="fas fa-flask"></i>Probar con un archivo de muestra
                        </div>
                        <div className="taf-card__hint">
                            Corre el agente de verdad con el prompt de una fila y trae las <b>claves</b> del resultado para mapearlas abajo.
                        </div>
                        {rows.length > 0 && (
                            <div className="taf-field">
                                <label className="taf-label">Probar con el prompt de la fila:</label>
                                <select className="taf-input" value={testItemId} onChange={e => setTestItemId(e.target.value)}>
                                    <option value="">— elige una fila —</option>
                                    {rows.map(row => (
                                        <option key={row.id} value={String(row.id)}>{row.name || `Fila ${row.position || ""}`}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="taf-test-row">
                            <input type="text" className="taf-input" value={sampleUrl}
                                onChange={e => setSampleUrl(e.target.value)}
                                placeholder="https://.../muestra.jpg" />
                            <button className="taf-btn taf-btn--soft" onClick={runTest} disabled={testing}>
                                {testing
                                    ? <><i className="fas fa-spinner fa-spin"></i>Probando…</>
                                    : <><i className="fas fa-play"></i>Probar</>}
                            </button>
                        </div>
                        {testResult && (
                            <div className="taf-test-result">
                                {testResult.resumen && (
                                    <div className="taf-test-result__resumen"><b>Resumen:</b> {testResult.resumen}</div>
                                )}
                                <div className="taf-card__hint" style={{ marginBottom: 4 }}>Datos devueltos:</div>
                                <pre className="taf-datos">{JSON.stringify(testResult.datos, null, 2)}</pre>
                            </div>
                        )}
                    </div>

                    {/* Mapeo → campo de la fila */}
                    <div className="taf-card">
                        <label className="taf-label">Mapeo (dato de la IA → campo de la fila)</label>
                        <div className="taf-card__hint" style={{ marginBottom: 10 }}>
                            {datosKeys.length > 0
                                ? <>Elige una clave devuelta por la IA y a qué campo de la fila se escribe.</>
                                : <>Escribe la clave del resultado (o usa <b>Probar</b>) y el campo destino de la fila.</>}
                        </div>
                        <datalist id={datalistId}>
                            {datosKeys.map(k => <option key={k} value={k} />)}
                        </datalist>
                        {config.mapeos.length === 0 && (
                            <div className="taf-empty">Sin mapeos.</div>
                        )}
                        {config.mapeos.map((m, idx) => (
                            <div className="taf-map-row" key={idx}>
                                <input type="text" className="taf-input taf-map-row__key" list={datalistId}
                                    value={m.clave}
                                    onChange={e => updateMapeo(idx, { clave: e.target.value })}
                                    placeholder="clave (p. ej. estado)" />
                                <i className="fas fa-arrow-right taf-map-row__arrow"></i>
                                <div className="taf-map-row__target">
                                    <select className="taf-input" value={m.target}
                                        onChange={e => updateMapeo(idx, { target: e.target.value })}>
                                        <option value="">Campo destino...</option>
                                        {TARGETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <button className="taf-map-del" onClick={() => removeMapeo(idx)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        ))}
                        <button className="taf-btn taf-btn--ghost taf-btn--sm" onClick={addMapeo}>
                            <i className="fas fa-plus"></i>Agregar mapeo
                        </button>
                    </div>

                    {/* Validación */}
                    <div className="taf-card" style={{ marginBottom: 0 }}>
                        <div className="custom-control custom-switch taf-switch" style={{ marginBottom: 8 }}>
                            <input type="checkbox" className="custom-control-input" id={`taf-m-cancel-${matrixId}`}
                                checked={config.validacion.si_no_coincide === "cancelar"}
                                onChange={e => patchValidacion({ si_no_coincide: e.target.checked ? "cancelar" : "nada" })} />
                            <label className="custom-control-label" htmlFor={`taf-m-cancel-${matrixId}`}>
                                Rechazar la evidencia si la IA responde <code className="taf-code">datos.coincide = false</code>
                            </label>
                        </div>
                        {config.validacion.si_no_coincide === "cancelar" && (
                            <input type="text" className="taf-input" value={config.validacion.mensaje}
                                onChange={e => patchValidacion({ mensaje: e.target.value })}
                                placeholder="Mensaje cuando se rechaza (p. ej. La foto no corresponde a la evidencia)." />
                        )}
                    </div>
                </div>
            </div>

            <div className="taf-footer">
                <button className="taf-btn taf-btn--ghost" onClick={toggle} disabled={saving}>Cancelar</button>
                <button className="taf-btn taf-btn--primary" onClick={save} disabled={saving}>
                    {saving
                        ? <><i className="fas fa-spinner fa-spin"></i>Guardando…</>
                        : <><i className="fas fa-save"></i>Guardar</>}
                </button>
            </div>
        </Modal>
    );
};

export default ModalTaimesAgentMatrix;
